import type { CampaignEconomicsReport, CampaignReleaseReview } from './api';
const money = (value: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value / 100);
const labels: Record<string, string> = {
  CATALOG: 'Store-Kataloge',
  JOURNALS: 'Erstelljournale',
  HISTORICAL_PURCHASES: 'Historische Rabattkäufe beider Stores',
  CURRENT_DEVICE_QA: 'Geräteabnahme der aktuellen Fassung',
  RECIPIENT_AND_CONTACT: 'Individuelle Berechtigung und Kontaktprüfung',
  BUDGET_CAPACITY: 'Rabattbudget-Kapazität',
  COST_PLANNING: 'Kostenplanung beider Stores',
  FINANCE_APPROVAL: 'Finanzprüfung',
  PUSH: 'Push-Einwilligung und Zustellweg',
  IMMUTABLE_APPROVAL_AND_STOP: 'Ausdrückliche unveränderliche Freigabe und Stopsteuerung',
};
const reasons: Record<string, string> = {
  INCOMPLETE: 'Grundlage unvollständig',
  TERMS_CHANGED: 'Tarif oder Preise passen nicht mehr',
  FUTURE_BASIS: 'Stand liegt in der Zukunft',
  EXPIRED: 'Grundlage abgelaufen',
  HORIZON_NOT_COVERED: 'Gültigkeit deckt die drei Rabattmonate nicht ab',
  NET_EXCEEDS_PRICE: 'Nettozufluss übersteigt den Brutto-Aktionspreis',
};
interface CampaignFinanceReportProps {
  economics?: CampaignEconomicsReport;
  review?: CampaignReleaseReview;
}
export function CampaignFinanceReport({ economics, review }: CampaignFinanceReportProps) {
  return (
    <section
      aria-label="Kosten und Freigaberegeln"
      className="mt-4 space-y-4 rounded-lg bg-bg-base p-4 text-sm"
    >
      <h3 className="font-semibold">Kosten und Freigaberegeln</h3>
      {!economics ? (
        <p>Dieser ältere Bericht enthält noch keine Kostenplanung. Nachweise erneut prüfen.</p>
      ) : (
        <>
          <p className="text-text-secondary">
            Vorsichtige Planungswerte, keine bestätigte Marge oder Finanzfreigabe. Annahme: drei
            erfolgreiche Rabattzahlungen. Kostengültigkeit erforderlich bis{' '}
            {new Date(economics.requiredThrough).toLocaleString('de-DE', {
              timeZone: 'Europe/Berlin',
            })}{' '}
            (Berlin).
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {economics.providers.map((p) => (
              <div key={p.provider}>
                <h4 className="font-semibold">
                  {p.provider === 'APP_STORE' ? 'Apple App Store' : 'Google Play'}
                </h4>
                {p.status === 'CALCULATED' &&
                p.monthlyContributionLowerMinor !== null &&
                p.threePaidMonthsContributionLowerMinor !== null &&
                p.minimumContributionMinor !== null ? (
                  <>
                    <p className="mt-2">
                      Plan-Deckungsbeitrag mindestens {money(p.monthlyContributionLowerMinor)} /
                      Monat; für drei bezahlte Monate{' '}
                      {money(p.threePaidMonthsContributionLowerMinor)}.
                    </p>
                    <p className={p.thresholdMet ? 'mt-2' : 'mt-2 text-warning'}>
                      Mindestwert {money(p.minimumContributionMinor)} / Monat:{' '}
                      {p.thresholdMet ? 'rechnerisch erreicht' : 'unterschritten'}.
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-warning">Keine belastbare Planrechnung.</p>
                )}
                {p.reasons.map((reason) => (
                  <p key={reason} className="mt-1 text-warning">
                    {reasons[reason] ?? 'Grundlage erneut prüfen'}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
      <h4 className="font-semibold">Serverseitige Checkliste · Freigabe gesperrt</h4>
      {!review ? (
        <p>Checkliste noch nicht erhoben. Nachweise erneut prüfen.</p>
      ) : (
        <ul className="space-y-2">
          {review.items.map((item) => (
            <li key={item.code}>
              {labels[item.code] ?? 'Weitere Prüfung'}:{' '}
              {(
                {
                  MATCHED: 'zum Prüfzeitpunkt passend',
                  CALCULATED: 'rechnerisch geprüft, keine Freigabe',
                  OPEN: 'offen',
                  NOT_REQUESTED: 'nicht angefordert',
                } as Record<string, string>
              )[item.status] ?? 'offen'}
            </li>
          ))}
        </ul>
      )}
      <p className="text-warning">
        Finanzprüfung und ausdrückliche Freigabe bleiben offen. Keine Aktivierung, Reservierung oder
        Nachricht.
      </p>
    </section>
  );
}
